import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Layout } from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  Plus,
  Search,
  Filter,
  MoreHorizontal,
  Eye,
  Edit,
  Trash2,
  UserCheck,
  Phone,
  Mail,
  Building2,
  Users
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const VisitorsPage = () => {
  const { user, chapter } = useAuth();
  const navigate = useNavigate();
  const [visitors, setVisitors] = useState([]);
  const [powerTeams, setPowerTeams] = useState([]);
  const [categories, setCategories] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingVisitor, setEditingVisitor] = useState(null);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [filterPT, setFilterPT] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterTag, setFilterTag] = useState("");
  const [filterInterest, setFilterInterest] = useState("");

  const token = localStorage.getItem("token");
  const primaryColor = chapter?.primary_color || "#CC0000";
  const canEdit = user?.role === "admin" || user?.role === "host";

  const [formData, setFormData] = useState({
    full_name: "",
    company: "",
    mobile: "",
    email: "",
    tag: "Visitor",
    power_team_id: "",
    category_id: "",
    invited_by_member_id: "",
    interest_level: "",
    assigned_to_member_id: "",
    notes: ""
  });

  useEffect(() => {
    fetchData();
  }, [filterPT, filterCategory, filterStatus, filterTag, filterInterest, searchQuery]);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      let url = `${API}/visitors?`;
      if (filterPT) url += `power_team_id=${filterPT}&`;
      if (filterCategory) url += `category_id=${filterCategory}&`;
      if (filterStatus) url += `status=${filterStatus}&`;
      if (filterTag) url += `tag=${filterTag}&`;
      if (filterInterest) url += `interest_level=${filterInterest}&`;
      if (searchQuery) url += `search=${searchQuery}&`;

      const [visitorsRes, ptRes, catRes, membersRes] = await Promise.all([
        fetch(url, { headers }),
        fetch(`${API}/power-teams`, { headers }),
        fetch(`${API}/categories`, { headers }),
        fetch(`${API}/members`, { headers })
      ]);

      if (visitorsRes.ok) setVisitors(await visitorsRes.json());
      if (ptRes.ok) setPowerTeams(await ptRes.json());
      if (catRes.ok) setCategories(await catRes.json());
      if (membersRes.ok) setMembers(await membersRes.json());
    } catch (error) {
      console.error("Fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editingVisitor 
        ? `${API}/visitors/${editingVisitor.visitor_id}`
        : `${API}/visitors`;
      
      const response = await fetch(url, {
        method: editingVisitor ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          ...formData,
          interest_level: formData.interest_level ? parseInt(formData.interest_level) : null
        })
      });

      if (response.ok) {
        toast.success(editingVisitor ? "Visitor updated" : "Visitor added");
        setDialogOpen(false);
        resetForm();
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Operation failed");
      }
    } catch (error) {
      toast.error("Error saving visitor");
    }
  };

  const handleDelete = async (visitorId) => {
    if (!confirm("Are you sure you want to delete this visitor?")) return;
    
    try {
      const response = await fetch(`${API}/visitors/${visitorId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success("Visitor deleted");
        fetchData();
      }
    } catch (error) {
      toast.error("Error deleting visitor");
    }
  };

  const handleConvert = async (visitorId) => {
    if (!confirm("Convert this visitor to a member? This action cannot be undone.")) return;
    
    try {
      const response = await fetch(`${API}/visitors/${visitorId}/convert`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success("Visitor converted to member!");
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Conversion failed");
      }
    } catch (error) {
      toast.error("Error converting visitor");
    }
  };

  const resetForm = () => {
    setFormData({
      full_name: "",
      company: "",
      mobile: "",
      email: "",
      tag: "Visitor",
      power_team_id: "",
      category_id: "",
      invited_by_member_id: "",
      interest_level: "",
      assigned_to_member_id: "",
      notes: ""
    });
    setEditingVisitor(null);
  };

  const openEditDialog = (visitor) => {
    setEditingVisitor(visitor);
    setFormData({
      full_name: visitor.full_name || "",
      company: visitor.company || "",
      mobile: visitor.mobile || "",
      email: visitor.email || "",
      tag: visitor.tag || "Visitor",
      power_team_id: visitor.power_team_id || "",
      category_id: visitor.category_id || "",
      invited_by_member_id: visitor.invited_by_member_id || "",
      interest_level: visitor.interest_level?.toString() || "",
      assigned_to_member_id: visitor.assigned_to_member_id || "",
      notes: visitor.notes || ""
    });
    setDialogOpen(true);
  };

  const getInterestBadge = (level) => {
    switch(level) {
      case 1: return <Badge className="bg-green-100 text-green-700 hover:bg-green-100">Level 1 - Interested</Badge>;
      case 2: return <Badge className="bg-yellow-100 text-yellow-700 hover:bg-yellow-100">Level 2 - Needs Permission</Badge>;
      case 3: return <Badge className="bg-red-100 text-red-700 hover:bg-red-100">Level 3 - Not Now</Badge>;
      default: return <Badge variant="outline">Not Set</Badge>;
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      Active: "bg-blue-100 text-blue-700",
      Converted: "bg-green-100 text-green-700",
      Lost: "bg-red-100 text-red-700",
      Nurture: "bg-purple-100 text-purple-700"
    };
    return <Badge className={`${colors[status] || "bg-slate-100 text-slate-700"} hover:${colors[status]}`}>{status}</Badge>;
  };

  const getPowerTeamName = (id) => powerTeams.find(pt => pt.power_team_id === id)?.name || "-";
  const getCategoryName = (id) => categories.find(c => c.category_id === id)?.name || "-";
  const getMemberName = (id) => members.find(m => m.member_id === id)?.full_name || "-";

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="visitors-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-display font-bold text-slate-900">Visitors</h1>
            <p className="text-muted-foreground mt-1">{visitors.length} total visitors</p>
          </div>
          
          {canEdit && (
            <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button style={{ backgroundColor: primaryColor }} className="hover:opacity-90" data-testid="add-visitor-btn">
                  <Plus className="mr-2 h-4 w-4" />
                  Add Visitor
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingVisitor ? "Edit Visitor" : "Add New Visitor"}</DialogTitle>
                  <DialogDescription>
                    {editingVisitor ? "Update visitor information and follow-up details." : "Register a new visitor to track their journey."}
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="full_name">Full Name *</Label>
                      <Input
                        id="full_name"
                        value={formData.full_name}
                        onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                        required
                        data-testid="visitor-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="company">Company</Label>
                      <Input
                        id="company"
                        value={formData.company}
                        onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                        data-testid="visitor-company-input"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="mobile">Mobile</Label>
                      <Input
                        id="mobile"
                        value={formData.mobile}
                        onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Tag</Label>
                      <Select value={formData.tag} onValueChange={(v) => setFormData({ ...formData, tag: v })}>
                        <SelectTrigger data-testid="visitor-tag-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Visitor">Visitor</SelectItem>
                          <SelectItem value="Guest">Guest</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Interest Level</Label>
                      <Select value={formData.interest_level} onValueChange={(v) => setFormData({ ...formData, interest_level: v })}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select level" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">1 - Interested & wants to join</SelectItem>
                          <SelectItem value="2">2 - Needs permission</SelectItem>
                          <SelectItem value="3">3 - Not the right time</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Power Team</Label>
                      <Select value={formData.power_team_id} onValueChange={(v) => setFormData({ ...formData, power_team_id: v })}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select power team" />
                        </SelectTrigger>
                        <SelectContent>
                          {powerTeams.filter(pt => pt.status === "active").map(pt => (
                            <SelectItem key={pt.power_team_id} value={pt.power_team_id}>{pt.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Category</Label>
                      <Select value={formData.category_id} onValueChange={(v) => setFormData({ ...formData, category_id: v })}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent>
                          {categories.filter(c => c.status === "active").map(cat => (
                            <SelectItem key={cat.category_id} value={cat.category_id}>{cat.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Invited By</Label>
                      <Select value={formData.invited_by_member_id} onValueChange={(v) => setFormData({ ...formData, invited_by_member_id: v })}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select member" />
                        </SelectTrigger>
                        <SelectContent>
                          {members.filter(m => m.status === "active").map(m => (
                            <SelectItem key={m.member_id} value={m.member_id}>{m.full_name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Assigned Follow-up To</Label>
                      <Select value={formData.assigned_to_member_id} onValueChange={(v) => setFormData({ ...formData, assigned_to_member_id: v })}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select member" />
                        </SelectTrigger>
                        <SelectContent>
                          {members.filter(m => m.status === "active").map(m => (
                            <SelectItem key={m.member_id} value={m.member_id}>{m.full_name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="notes">Notes</Label>
                    <Textarea
                      id="notes"
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      rows={3}
                    />
                  </div>

                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" style={{ backgroundColor: primaryColor }} data-testid="save-visitor-btn">
                      {editingVisitor ? "Update" : "Add"} Visitor
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Filters */}
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex flex-wrap gap-4">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search visitors..."
                    className="pl-10"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    data-testid="visitor-search-input"
                  />
                </div>
              </div>
              
              <Select value={filterPT || "all"} onValueChange={(v) => setFilterPT(v === "all" ? "" : v)}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Power Team" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Power Teams</SelectItem>
                  {powerTeams.map(pt => (
                    <SelectItem key={pt.power_team_id} value={pt.power_team_id}>{pt.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filterCategory || "all"} onValueChange={(v) => setFilterCategory(v === "all" ? "" : v)}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat.category_id} value={cat.category_id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filterStatus || "all"} onValueChange={(v) => setFilterStatus(v === "all" ? "" : v)}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Converted">Converted</SelectItem>
                  <SelectItem value="Lost">Lost</SelectItem>
                  <SelectItem value="Nurture">Nurture</SelectItem>
                </SelectContent>
              </Select>

              <Select value={filterTag || "all"} onValueChange={(v) => setFilterTag(v === "all" ? "" : v)}>
                <SelectTrigger className="w-[130px]">
                  <SelectValue placeholder="Tag" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Tags</SelectItem>
                  <SelectItem value="Visitor">Visitor</SelectItem>
                  <SelectItem value="Guest">Guest</SelectItem>
                </SelectContent>
              </Select>

              <Select value={filterInterest || "all"} onValueChange={(v) => setFilterInterest(v === "all" ? "" : v)}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Interest" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Levels</SelectItem>
                  <SelectItem value="1">Level 1</SelectItem>
                  <SelectItem value="2">Level 2</SelectItem>
                  <SelectItem value="3">Level 3</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Visitors Table */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="visitors-table">
                <thead>
                  <tr className="border-b bg-slate-50">
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground">Name</th>
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground hidden md:table-cell">Company</th>
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground hidden lg:table-cell">Power Team</th>
                    <th className="text-center py-3 px-4 font-medium text-muted-foreground">Interest</th>
                    <th className="text-center py-3 px-4 font-medium text-muted-foreground">Status</th>
                    <th className="text-center py-3 px-4 font-medium text-muted-foreground hidden sm:table-cell">Tag</th>
                    <th className="text-right py-3 px-4 font-medium text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visitors.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="text-center py-12 text-muted-foreground">
                        No visitors found. Add your first visitor to get started.
                      </td>
                    </tr>
                  ) : (
                    visitors.map((visitor, index) => (
                      <tr 
                        key={visitor.visitor_id} 
                        className="border-b hover:bg-slate-50 cursor-pointer animate-fade-in"
                        style={{ animationDelay: `${index * 0.03}s` }}
                        onClick={() => navigate(`/visitors/${visitor.visitor_id}`)}
                        data-testid={`visitor-row-${visitor.visitor_id}`}
                      >
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-3">
                            <div 
                              className="w-10 h-10 rounded-full flex items-center justify-center text-white font-medium"
                              style={{ backgroundColor: primaryColor }}
                            >
                              {visitor.full_name?.charAt(0)?.toUpperCase()}
                            </div>
                            <div>
                              <p className="font-medium text-slate-900">{visitor.full_name}</p>
                              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                {visitor.mobile && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{visitor.mobile}</span>}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4 hidden md:table-cell">
                          <span className="text-slate-600">{visitor.company || "-"}</span>
                        </td>
                        <td className="py-3 px-4 hidden lg:table-cell">
                          <span className="text-slate-600">{getPowerTeamName(visitor.power_team_id)}</span>
                        </td>
                        <td className="py-3 px-4 text-center">
                          {getInterestBadge(visitor.interest_level)}
                        </td>
                        <td className="py-3 px-4 text-center">
                          {getStatusBadge(visitor.status)}
                        </td>
                        <td className="py-3 px-4 text-center hidden sm:table-cell">
                          <Badge variant="outline">{visitor.tag}</Badge>
                        </td>
                        <td className="py-3 px-4 text-right" onClick={(e) => e.stopPropagation()}>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => navigate(`/visitors/${visitor.visitor_id}`)}>
                                <Eye className="mr-2 h-4 w-4" />
                                View Details
                              </DropdownMenuItem>
                              {canEdit && (
                                <>
                                  <DropdownMenuItem onClick={() => openEditDialog(visitor)}>
                                    <Edit className="mr-2 h-4 w-4" />
                                    Edit
                                  </DropdownMenuItem>
                                  {visitor.status === "Active" && user?.role === "admin" && (
                                    <DropdownMenuItem onClick={() => handleConvert(visitor.visitor_id)}>
                                      <UserCheck className="mr-2 h-4 w-4" />
                                      Convert to Member
                                    </DropdownMenuItem>
                                  )}
                                  {user?.role === "admin" && (
                                    <DropdownMenuItem 
                                      onClick={() => handleDelete(visitor.visitor_id)}
                                      className="text-red-600"
                                    >
                                      <Trash2 className="mr-2 h-4 w-4" />
                                      Delete
                                    </DropdownMenuItem>
                                  )}
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default VisitorsPage;
